import { useEffect } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';

import welcomeCoinPiles from '../../../assets/welcome-coins-piles.png';
import Button from '../../../components/buttons/Button';
import { DialogBox } from '../../../components/Dialog/DialogBox';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import { DialogLoader } from '../../../components/Dialog/DialogLoader';
import { SocialCoin } from '../../../components/SocialCoin/SocialCoin';
import { SweepCoin } from '../../../components/SweepCoin/SweepCoin';
import type { WizardChildProps } from '../../../components/Wizard/types';
import { useMissedWelcomeBonus } from '../../../hooks/useMissedWelcomeBonus';
import { analytics } from '../../../services/analytics';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';
import { formatValueWithK } from '../../../utils/helpers';
import { devError } from '../../../utils/log';

import styles from './WelcomeBonusDialog.module.scss';

type Props = WizardChildProps | { componentType: 'dialog' | 'portal' } | Record<string, never>;

const WelcomeBonusDialog = observer((props: Props) => {
  const { t } = useTranslation();
  const { rewardsStore, portalStore, uiStore } = useStores();
  const isSignUpCountrySelectionEnabled = useFlag<boolean>('sign_up_flow_country_selection', false);
  const isScModeAvailable = useFlag<boolean>('sc_mode_available', true);
  const { removeMissedWelcomeBonusUserData } = useMissedWelcomeBonus();
  const isActive = 'isCurrent' in props && props.isCurrent === true;

  useEffectOnce(() => {
    removeMissedWelcomeBonusUserData();
  });

  useEffect(() => {
    if (isActive) {
      void analytics.sendEvent('welcome_gift', {
        event_category: 'sign_up',
        event_action: 'impression',
        button_text: 'claim',
      });
    }
  }, [isActive]);

  useEffect(() => {
    if (!rewardsStore.welcomeBonusLoading && !rewardsStore.welcomeBonus) {
      devError('WelcomeBonusDialog: welcomeBonus is not loaded, closing dialog');
      portalStore.closePortal();
    }
  }, [rewardsStore.welcomeBonus, rewardsStore.welcomeBonusLoading, portalStore]);

  const gcAmount = formatValueWithK(rewardsStore.welcomeBonus?.gcAmount || 0, t);
  const scAmount = (rewardsStore.welcomeBonus?.scAmount || 0).toLocaleString();

  const onSubmit = () => {
    void analytics.sendEvent('welcome_gift', {
      event_category: 'sign_up',
      event_action: 'click',
      button_text: 'claim',
    });
    if (props && 'componentType' in props && props.componentType === 'dialog') {
      uiStore.setOpenDialog({ dialog: null });
      return;
    }
    if (isSignUpCountrySelectionEnabled && props && 'nextStep' in props) {
      props.nextStep();
    } else {
      portalStore.closePortal();
    }
  };

  return (
    <DialogBox>
      <DialogHeader title={t('welcome.titleLowerCase')} onClose={onSubmit} />

      <DialogLoader isProcessing={rewardsStore.welcomeBonusLoading} />

      <DialogContent>
        <div className={styles.welcomeBonusWrapper}>
          <span className={styles.yourText}>{t('common.buttons.claimYour')}</span>
          <div className={styles.coinAmountsWrapper}>
            <SocialCoin alt="social coin" className={styles.coinSymbol} />
            <span className={styles.coinsAmount}>{gcAmount}</span>
            {isScModeAvailable && (
              <>
                <span className={styles.coinsAmount}>&nbsp;+</span>
                <SweepCoin alt="sweep coin" className={styles.coinSymbol} />
                <span className={styles.coinsAmount}>{scAmount}</span>
              </>
            )}
          </div>
        </div>

        <img alt="" className={styles.coinPileImage} src={welcomeCoinPiles} />
        <Button btnType="main" onClick={onSubmit}>
          {t('common.buttons.claimNowLowerCase')}
        </Button>
      </DialogContent>
    </DialogBox>
  );
});

export default WelcomeBonusDialog;
